/**
 * Special Collections Module — Phase X1 frontend.
 *
 * Lives at /app/frontend/src/pages/specialcollections/SpecialCollections.jsx.
 * Single-file module with sub-routes for Dashboard, Collect, Daily Register,
 * Reports (By Type / By Cashier / By Payer / Void Log) and Catalog. Built as
 * an independent UI surface that does not import or reference any monthly
 * fee component, page, or service. The sidebar entry is rendered behind a
 * runtime feature-flag check against `/api/school/special-collections/types`
 * — a 404 means the flag is off, hiding the nav group entirely.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import {
  Wallet, Receipt, ChartBar, Users as UsersIcon, ClipboardText, Warning,
  Plus, MagnifyingGlass, Printer, DownloadSimple, XCircle, Tag,
} from "@phosphor-icons/react";
import api, { API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const INR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return iso;
  }
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// =========================================================================
// Layout — shared header + sub-nav
// =========================================================================

// IMPORTANT: use ABSOLUTE paths here. The SC module is mounted as a
// descendant <Routes> under the splat route `/special-collections/*`
// (see App.js). With descendant Routes, react-router v7 resolves relative
// `<NavLink to="x">` against the deepest matched leaf, which makes
// tab-to-tab navigation append (e.g. /special-collections/collect/daily-register)
// instead of replacing the segment. Absolute paths bypass that resolution
// entirely and also make the Dashboard `end`-match well-behaved.
const SC_BASE = "/special-collections";
const SUB_NAV = [
  { to: `${SC_BASE}`, label: "Dashboard", icon: Wallet, end: true },
  { to: `${SC_BASE}/collect`, label: "Collect", icon: Plus },
  { to: `${SC_BASE}/daily-register`, label: "Daily Register", icon: ClipboardText },
  { to: `${SC_BASE}/by-type`, label: "By Type", icon: Tag },
  { to: `${SC_BASE}/by-cashier`, label: "By Cashier", icon: UsersIcon },
  { to: `${SC_BASE}/by-payer`, label: "By Payer", icon: UsersIcon },
  { to: `${SC_BASE}/void-log`, label: "Void Log", icon: Warning },
  { to: `${SC_BASE}/catalog`, label: "Catalog", icon: ChartBar, adminOnly: true },
];

export function SpecialCollectionsLayout() {
  const { user } = useAuth();
  return (
    <div className="space-y-6" data-testid="sc-layout">
      <header className="border-b border-slate-200 pb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
              Independent of monthly fees
            </p>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">
              Special Collections
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Admission fees, fines, certificates, recovery, and miscellaneous
              receipts. Not part of any monthly fee report.
            </p>
          </div>
        </div>
        <nav className="mt-4 -mb-px flex flex-wrap gap-1 border-b border-slate-200/70">
          {SUB_NAV.filter((n) => !(n.adminOnly && user?.role === "cashier")).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`sc-tab-${(n.label || "").toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ` +
                (isActive
                  ? "border-sky-600 text-sky-700"
                  : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300")
              }
            >
              <n.icon size={16} weight="duotone" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <Outlet />
    </div>
  );
}

// =========================================================================
// Dashboard
// =========================================================================

function StatCard({ label, amount, count, hint, tone = "slate", testid }) {
  return (
    <div
      className={`rounded-xl border border-${tone}-200 bg-white p-4 shadow-sm`}
      data-testid={testid}
    >
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-2xl font-semibold text-slate-900 mt-1">{INR(amount)}</p>
      <p className="text-xs text-slate-500 mt-1">
        {count != null ? `${count} receipt${count === 1 ? "" : "s"}` : ""}
        {hint ? ` · ${hint}` : ""}
      </p>
    </div>
  );
}

export function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/school/special-collections/dashboard");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (err) return <div className="text-rose-600 text-sm" data-testid="sc-dash-err">{err}</div>;
  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="sc-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Today" amount={data.today.amount} count={data.today.count}
                  hint={data.today.ist_date} tone="sky" testid="sc-today" />
        <StatCard label="This Month" amount={data.mtd.amount} count={data.mtd.count}
                  hint={data.mtd.period_month} tone="violet" testid="sc-mtd" />
        <StatCard label="Financial YTD" amount={data.fytd.amount} count={data.fytd.count}
                  hint={`FY ${data.fytd.fy}`} tone="emerald" testid="sc-fytd" />
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-700">MTD by collection type</h3>
        {data.by_type_mtd.length === 0 ? (
          <p className="text-sm text-slate-500 mt-2">No collections this month yet.</p>
        ) : (
          <table className="w-full text-sm mt-3" data-testid="sc-by-type-mtd">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-2">Type</th>
                <th className="text-right py-2">Count</th>
                <th className="text-right py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.by_type_mtd.map((r) => (
                <tr key={r.type_id} className="border-t border-slate-100">
                  <td className="py-2">{r.type_name}</td>
                  <td className="py-2 text-right">{r.count}</td>
                  <td className="py-2 text-right font-medium">{INR(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-700">Recent receipts</h3>
        {data.recent_receipts.length === 0 ? (
          <p className="text-sm text-slate-500 mt-2">No receipts yet.</p>
        ) : (
          <table className="w-full text-sm mt-3" data-testid="sc-recent">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-2">Receipt</th>
                <th className="py-2">When</th>
                <th className="py-2">Payer</th>
                <th className="py-2">Type</th>
                <th className="py-2">Mode</th>
                <th className="text-right py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_receipts.map((r) => (
                <tr key={r._id} className="border-t border-slate-100">
                  <td className="py-2 font-mono text-xs">{r.receipt_no}</td>
                  <td className="py-2">{fmtIST(r.paid_at)}</td>
                  <td className="py-2">{r.payer_name}</td>
                  <td className="py-2">{r.type_name}</td>
                  <td className="py-2 uppercase text-xs">{r.payment_mode}</td>
                  <td className="py-2 text-right font-medium">{INR(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <p className="text-xs text-slate-500">
        Special Collections is an independent cash ledger. These numbers do not
        appear in any monthly fee report, dashboard, or defaulter list.
      </p>
    </div>
  );
}

// =========================================================================
// Collect dialog page
// =========================================================================

export function Collect() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [types, setTypes] = useState([]);
  const [payerKind, setPayerKind] = useState("student"); // student | external
  const [studentResults, setStudentResults] = useState([]);
  const [studentQ, setStudentQ] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [payerForm, setPayerForm] = useState({
    display_name: "", father_name: "", phone: "",
    last_known_class: "", last_known_admission_no: "", notes: "",
  });
  const [createdPayer, setCreatedPayer] = useState(null);
  const [receiptForm, setReceiptForm] = useState({
    type_id: "", amount: "", payment_mode: "cash",
    txn_ref: "", notes: "", paid_at: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [idempKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    api.get("/school/special-collections/types?active_only=true")
      .then(({ data }) => setTypes(data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const onStudentSearch = useCallback(async (q) => {
    if (q.trim().length < 2) { setStudentResults([]); return; }
    try {
      const { data } = await api.get(
        `/school/special-collections/payers/student-search?q=${encodeURIComponent(q)}`,
      );
      setStudentResults(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const submitPayer = async () => {
    try {
      let payload;
      if (payerKind === "student") {
        if (!selectedStudent) {
          toast.error("Pick a student first");
          return;
        }
        payload = {
          linked_student_id: selectedStudent.student_id,
          display_name: selectedStudent.name,
          father_name: selectedStudent.parent_name || null,
          phone: selectedStudent.parent_phone || null,
          last_known_class: selectedStudent.class_label,
          last_known_admission_no: selectedStudent.admission_no,
        };
      } else {
        if (!payerForm.display_name.trim()) {
          toast.error("Display name required"); return;
        }
        if (!payerForm.phone.trim()) {
          toast.error("Phone is required for external payers"); return;
        }
        payload = { ...payerForm };
      }
      const { data } = await api.post("/school/special-collections/payers", payload);
      setCreatedPayer(data);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitReceipt = async () => {
    if (!receiptForm.type_id) { toast.error("Pick a type"); return; }
    const amt = parseFloat(receiptForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    setSubmitting(true);
    try {
      const payload = {
        payer_id: createdPayer._id,
        type_id: receiptForm.type_id,
        amount: amt,
        payment_mode: receiptForm.payment_mode,
        txn_ref: receiptForm.txn_ref || null,
        notes: receiptForm.notes || null,
        paid_at: receiptForm.paid_at ? new Date(receiptForm.paid_at).toISOString() : null,
        idempotency_key: idempKey,
      };
      const { data } = await api.post("/school/special-collections/receipts", payload);
      toast.success(`Saved · ${data.receipt_no}`);
      // Open the PDF in a new tab
      try {
        const t = tokenStore.get();
        const resp = await fetch(
          `${API_BASE}/school/special-collections/receipts/${data._id}/pdf`,
          { headers: { Authorization: `Bearer ${t}` } },
        );
        const blob = await resp.blob();
        window.open(URL.createObjectURL(blob), "_blank");
      } catch { /* ignore */ }
      navigate("/special-collections/daily-register");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = useMemo(
    () => types.find((t) => t._id === receiptForm.type_id),
    [types, receiptForm.type_id],
  );
  useEffect(() => {
    if (selectedType && selectedType.default_amount && !receiptForm.amount) {
      setReceiptForm((f) => ({ ...f, amount: String(selectedType.default_amount) }));
    }
  }, [selectedType, receiptForm.amount]);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="sc-collect">
      {step === 1 && (
        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <h3 className="text-base font-semibold">Step 1 · Choose payer</h3>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={payerKind === "student" ? "default" : "outline"}
              onClick={() => setPayerKind("student")}
              data-testid="sc-payer-kind-student"
            >Current / Former Student</Button>
            <Button
              type="button"
              variant={payerKind === "external" ? "default" : "outline"}
              onClick={() => setPayerKind("external")}
              data-testid="sc-payer-kind-external"
            >External Payer</Button>
          </div>

          {payerKind === "student" ? (
            <div className="space-y-3">
              <div>
                <Label>Search by name or admission no.</Label>
                <Input
                  value={studentQ}
                  onChange={(e) => { setStudentQ(e.target.value); onStudentSearch(e.target.value); }}
                  placeholder="Type at least 2 characters"
                  data-testid="sc-student-search-input"
                />
              </div>
              <ul className="rounded-md border border-slate-200 divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="sc-student-search-results">
                {studentResults.map((s) => (
                  <li
                    key={s.student_id}
                    className={`px-3 py-2 cursor-pointer text-sm ${
                      selectedStudent?.student_id === s.student_id ? "bg-sky-50" : ""
                    }`}
                    onClick={() => setSelectedStudent(s)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium text-slate-900">{s.name}
                          {s.is_active === false && <Badge variant="outline" className="ml-2">Former</Badge>}
                        </div>
                        <div className="text-xs text-slate-500">
                          {s.admission_no} · {s.class_label || "—"} · {s.session_name || ""}
                        </div>
                      </div>
                      {selectedStudent?.student_id === s.student_id && (
                        <Badge>Selected</Badge>
                      )}
                    </div>
                  </li>
                ))}
                {studentQ.length >= 2 && studentResults.length === 0 && (
                  <li className="px-3 py-2 text-sm text-slate-500">No matches.</li>
                )}
              </ul>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Payer name *</Label>
                <Input value={payerForm.display_name}
                       onChange={(e) => setPayerForm({ ...payerForm, display_name: e.target.value })}
                       data-testid="sc-external-name" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={payerForm.phone}
                       onChange={(e) => setPayerForm({ ...payerForm, phone: e.target.value })}
                       data-testid="sc-external-phone" />
              </div>
              <div>
                <Label>Father's name</Label>
                <Input value={payerForm.father_name}
                       onChange={(e) => setPayerForm({ ...payerForm, father_name: e.target.value })} />
              </div>
              <div>
                <Label>Last known class</Label>
                <Input value={payerForm.last_known_class}
                       onChange={(e) => setPayerForm({ ...payerForm, last_known_class: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Notes</Label>
                <Textarea value={payerForm.notes} rows={2}
                          onChange={(e) => setPayerForm({ ...payerForm, notes: e.target.value })} />
              </div>
            </div>
          )}
          <Button onClick={submitPayer} data-testid="sc-next-to-receipt">Next →</Button>
        </section>
      )}

      {step === 2 && createdPayer && (
        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold">Step 2 · Collection details</h3>
            <Button variant="ghost" size="sm" onClick={() => setStep(1)}>← Edit payer</Button>
          </div>
          <div className="text-sm bg-slate-50 rounded-md p-3 border border-slate-200">
            <div className="font-medium">{createdPayer.display_name}
              {createdPayer.is_external && <Badge variant="outline" className="ml-2">External</Badge>}
            </div>
            <div className="text-slate-500 text-xs mt-1">
              {createdPayer.phone || "no phone"} · {createdPayer.last_known_class || ""}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Collection type *</Label>
              <Select value={receiptForm.type_id}
                      onValueChange={(v) => setReceiptForm({ ...receiptForm, type_id: v })}>
                <SelectTrigger data-testid="sc-type-select"><SelectValue placeholder="Choose type" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t._id} value={t._id}>{t.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (₹) *</Label>
              <Input inputMode="decimal" type="number" min="0" step="0.01"
                     value={receiptForm.amount}
                     onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })}
                     data-testid="sc-amount-input" />
            </div>
            <div>
              <Label>Payment mode *</Label>
              <Select value={receiptForm.payment_mode}
                      onValueChange={(v) => setReceiptForm({ ...receiptForm, payment_mode: v })}>
                <SelectTrigger data-testid="sc-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash", "upi", "bank", "cheque", "card"].map((m) => (
                    <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {receiptForm.payment_mode !== "cash" && (
              <div>
                <Label>Transaction reference</Label>
                <Input value={receiptForm.txn_ref}
                       onChange={(e) => setReceiptForm({ ...receiptForm, txn_ref: e.target.value })}
                       data-testid="sc-txnref-input" />
              </div>
            )}
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={receiptForm.notes}
                        onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })} />
            </div>
          </div>

          <Button onClick={submitReceipt} disabled={submitting} data-testid="sc-save-receipt">
            {submitting ? "Saving…" : "Save & Print"}
          </Button>
          <p className="text-xs text-slate-500">
            On save, the receipt PDF opens in a new tab. Hand the printed copy
            to the payer at collection time — Special Collections receipts are
            not available to parents online.
          </p>
        </section>
      )}
    </div>
  );
}

// =========================================================================
// Filter bar (shared across reports)
// =========================================================================

function FilterBar({ value, onChange, extra }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end" data-testid="sc-filterbar">
      <div>
        <Label>From</Label>
        <Input type="date" value={value.date_from || ""} onChange={(e) => onChange({ ...value, date_from: e.target.value })}
               data-testid="sc-filter-from" />
      </div>
      <div>
        <Label>To</Label>
        <Input type="date" value={value.date_to || ""} onChange={(e) => onChange({ ...value, date_to: e.target.value })}
               data-testid="sc-filter-to" />
      </div>
      {extra}
    </div>
  );
}

function downloadJSONasCSV(rows, columns, filename) {
  const header = columns.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(",");
  const body = rows.map((r) =>
    columns.map((c) => {
      const v = c.value(r);
      const s = v == null ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    }).join(",")
  ).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// =========================================================================
// Daily Register
// =========================================================================

export function DailyRegister() {
  const [filter, setFilter] = useState({
    date_from: todayIso(), date_to: todayIso(), receipt_no: "",
  });
  const [data, setData] = useState({ items: [], total: 0, count: 0 });
  const [voidTarget, setVoidTarget] = useState(null);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.date_from) params.set("date_from", filter.date_from);
      if (filter.date_to) params.set("date_to", filter.date_to);
      if (filter.receipt_no) params.set("receipt_no", filter.receipt_no);
      const ep = filter.receipt_no
        ? `/school/special-collections/receipts?${params.toString()}`
        : `/school/special-collections/reports/daily-register?${params.toString()}`;
      const { data } = await api.get(ep);
      if (filter.receipt_no) {
        setData({ items: data.items || [], total: (data.items || []).reduce((s, r) => s + (r.amount || 0), 0),
                  count: (data.items || []).length });
      } else {
        setData(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="sc-daily">
      <FilterBar
        value={filter}
        onChange={setFilter}
        extra={
          <>
            <div>
              <Label>Receipt number</Label>
              <Input value={filter.receipt_no || ""}
                     onChange={(e) => setFilter({ ...filter, receipt_no: e.target.value })}
                     placeholder="SC-2026-…" data-testid="sc-filter-receipt-no" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => downloadJSONasCSV(
                data.items,
                [
                  { label: "Receipt No", value: (r) => r.receipt_no },
                  { label: "Date/Time IST", value: (r) => fmtIST(r.paid_at) },
                  { label: "Payer", value: (r) => r.payer_name_snapshot },
                  { label: "Type", value: (r) => r.type_name_snapshot },
                  { label: "Amount", value: (r) => r.amount },
                  { label: "Mode", value: (r) => r.payment_mode },
                  { label: "Txn Ref", value: (r) => r.txn_ref || "" },
                  { label: "Notes", value: (r) => r.notes || "" },
                  { label: "Collected By", value: (r) => r.collected_by_name },
                  { label: "Voided", value: (r) => r.is_voided ? "yes" : "" },
                ],
                `sc-daily-${filter.date_from}.csv`,
              )}>
                <DownloadSimple size={14} className="mr-1" />CSV
              </Button>
            </div>
          </>
        }
      />
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2">Receipt</th>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Payer</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Mode</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="px-3 py-2">Collector</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r._id} className={`border-t border-slate-100 ${r.is_voided ? "opacity-50" : ""}`} data-testid={`sc-row-${r.receipt_no}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.receipt_no}</td>
                <td className="px-3 py-2">{fmtIST(r.paid_at)}</td>
                <td className="px-3 py-2">{r.payer_name_snapshot}</td>
                <td className="px-3 py-2">{r.type_name_snapshot}</td>
                <td className="px-3 py-2 uppercase text-xs">{r.payment_mode}</td>
                <td className="px-3 py-2 text-right font-medium">{INR(r.amount)}</td>
                <td className="px-3 py-2 text-xs">{r.collected_by_name}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  <a className="inline-flex items-center text-sky-600 hover:underline text-xs"
                     href={`${API_BASE}/school/special-collections/receipts/${r._id}/pdf`}
                     target="_blank" rel="noreferrer"
                     onClick={async (e) => {
                       e.preventDefault();
                       const t = tokenStore.get();
                       const resp = await fetch(e.currentTarget.href,
                         { headers: { Authorization: `Bearer ${t}` } });
                       const b = await resp.blob();
                       window.open(URL.createObjectURL(b), "_blank");
                     }}>
                    <Printer size={14} className="mr-1" />PDF
                  </a>
                  {user?.role !== "cashier" && !r.is_voided && (
                    <button className="inline-flex items-center text-rose-600 hover:underline text-xs"
                            onClick={() => setVoidTarget(r)}
                            data-testid={`sc-void-${r.receipt_no}`}>
                      <XCircle size={14} className="mr-1" />Void
                    </button>
                  )}
                  {r.is_voided && <Badge variant="outline" className="text-rose-600 border-rose-200">VOIDED</Badge>}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No receipts in window.</td></tr>
            )}
          </tbody>
          <tfoot className="bg-slate-50">
            <tr className="font-medium">
              <td className="px-3 py-2" colSpan={5}>Total ({data.count} receipts)</td>
              <td className="px-3 py-2 text-right">{INR(data.total)}</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <VoidDialog
        open={!!voidTarget}
        receipt={voidTarget}
        onClose={() => setVoidTarget(null)}
        onVoided={() => { setVoidTarget(null); load(); }}
      />
    </div>
  );
}

function VoidDialog({ open, receipt, onClose, onVoided }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open]);

  if (!receipt) return null;
  const reasonOk = reason.trim().length >= 10;
  const submit = async () => {
    if (!reasonOk) { toast.error("Reason must be at least 10 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/school/special-collections/receipts/${receipt._id}/void`, { reason });
      toast.success("Receipt voided");
      onVoided();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sc-void-dialog">
        <DialogHeader>
          <DialogTitle>Void Receipt {receipt.receipt_no}?</DialogTitle>
          <DialogDescription>
            This receipt will be marked as voided. It will be excluded from
            collection totals but retained for audit.
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (min 10 characters)" data-testid="sc-void-reason" />
        <p className="text-xs text-slate-500">{reason.trim().length}/10 characters minimum</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy || !reasonOk} data-testid="sc-void-confirm">
            {busy ? "Voiding…" : "Void Receipt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// Reports: By Type / By Cashier / By Payer / Void Log
// =========================================================================

export function ByType() {
  const [filter, setFilter] = useState({ date_from: todayIso().slice(0, 8) + "01", date_to: todayIso() });
  const [data, setData] = useState({ totals: { amount: 0, count: 0 }, by_type: [] });
  useEffect(() => {
    const p = new URLSearchParams();
    if (filter.date_from) p.set("date_from", filter.date_from);
    if (filter.date_to) p.set("date_to", filter.date_to);
    api.get(`/school/special-collections/reports/by-type?${p.toString()}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [filter]);
  return (
    <div className="space-y-4" data-testid="sc-by-type">
      <FilterBar value={filter} onChange={setFilter} />
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Count</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-right px-3 py-2">Share</th>
            </tr>
          </thead>
          <tbody>
            {(data.by_type || []).map((r) => (
              <tr key={r.type_id} className="border-t border-slate-100">
                <td className="px-3 py-2">{r.type_name}</td>
                <td className="px-3 py-2 text-right">{r.count}</td>
                <td className="px-3 py-2 text-right font-medium">{INR(r.amount)}</td>
                <td className="px-3 py-2 text-right">{r.share_pct}%</td>
              </tr>
            ))}
            {(data.by_type || []).length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">No data.</td></tr>
            )}
          </tbody>
          <tfoot className="bg-slate-50 font-medium">
            <tr>
              <td className="px-3 py-2">Total</td>
              <td className="px-3 py-2 text-right">{data.totals?.count || 0}</td>
              <td className="px-3 py-2 text-right">{INR(data.totals?.amount)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export function ByCashier() {
  const [filter, setFilter] = useState({ date_from: todayIso().slice(0, 8) + "01", date_to: todayIso() });
  const [data, setData] = useState({ totals: { amount: 0, count: 0 }, by_cashier: [] });
  useEffect(() => {
    const p = new URLSearchParams();
    if (filter.date_from) p.set("date_from", filter.date_from);
    if (filter.date_to) p.set("date_to", filter.date_to);
    api.get(`/school/special-collections/reports/by-cashier?${p.toString()}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [filter]);
  return (
    <div className="space-y-4" data-testid="sc-by-cashier">
      <FilterBar value={filter} onChange={setFilter} />
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2">Cashier</th>
              <th className="px-3 py-2">Role</th>
              <th className="text-right px-3 py-2">Receipts</th>
              <th className="text-right px-3 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(data.by_cashier || []).map((r) => (
              <tr key={r.collected_by} className="border-t border-slate-100">
                <td className="px-3 py-2">{r.collected_by_name || r.collected_by}</td>
                <td className="px-3 py-2 text-xs">{(r.collected_by_role || "").replace("_", " ")}</td>
                <td className="px-3 py-2 text-right">{r.count}</td>
                <td className="px-3 py-2 text-right font-medium">{INR(r.amount)}</td>
              </tr>
            ))}
            {(data.by_cashier || []).length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">No data.</td></tr>
            )}
          </tbody>
          <tfoot className="bg-slate-50 font-medium">
            <tr>
              <td className="px-3 py-2" colSpan={2}>Total</td>
              <td className="px-3 py-2 text-right">{data.totals?.count || 0}</td>
              <td className="px-3 py-2 text-right">{INR(data.totals?.amount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export function ByPayer() {
  const [q, setQ] = useState("");
  const [payers, setPayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);

  const search = useCallback(async (val) => {
    if ((val || "").trim().length < 2) { setPayers([]); return; }
    try {
      const { data } = await api.get(`/school/special-collections/payers?q=${encodeURIComponent(val)}`);
      setPayers(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/school/special-collections/reports/by-payer?payer_id=${selected._id}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [selected]);

  return (
    <div className="space-y-4" data-testid="sc-by-payer">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Label>Search payer</Label>
          <Input value={q} onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
                 placeholder="Type at least 2 characters" data-testid="sc-bp-search" />
        </div>
      </div>
      {payers.length > 0 && !selected && (
        <ul className="rounded-md border border-slate-200 divide-y max-h-64 overflow-y-auto">
          {payers.map((p) => (
            <li key={p._id} className="px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
                onClick={() => setSelected(p)} data-testid={`sc-bp-row-${p._id}`}>
              {p.display_name}
              {p.is_external && <Badge variant="outline" className="ml-2">External</Badge>}
              <span className="text-xs text-slate-500 ml-2">{p.phone}</span>
            </li>
          ))}
        </ul>
      )}
      {selected && data && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold">{selected.display_name}</h3>
                <p className="text-xs text-slate-500">
                  {selected.is_external ? "External payer" : "Linked to student"} · {selected.phone || "—"}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setData(null); }}>Clear</Button>
            </div>
            <p className="mt-3 text-sm">
              Total: <span className="font-semibold">{INR(data.totals?.amount)}</span> across {data.totals?.count} receipts
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-600">
                  <th className="px-3 py-2">Receipt</th>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="text-right px-3 py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(data.receipts || []).map((r) => (
                  <tr key={r._id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">{r.receipt_no}</td>
                    <td className="px-3 py-2">{fmtIST(r.paid_at)}</td>
                    <td className="px-3 py-2">{r.type_name_snapshot}</td>
                    <td className="px-3 py-2 text-right">{INR(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function VoidLog() {
  const [filter, setFilter] = useState({ date_from: "", date_to: "" });
  const [data, setData] = useState({ items: [], total: 0, count: 0 });
  useEffect(() => {
    const p = new URLSearchParams();
    if (filter.date_from) p.set("date_from", filter.date_from);
    if (filter.date_to) p.set("date_to", filter.date_to);
    api.get(`/school/special-collections/reports/void-log?${p.toString()}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [filter]);

  return (
    <div className="space-y-4" data-testid="sc-void-log">
      <FilterBar value={filter} onChange={setFilter} />
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2">Receipt</th>
              <th className="px-3 py-2">Originally paid</th>
              <th className="px-3 py-2">Voided</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Voided by</th>
              <th className="text-right px-3 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(data.items || []).map((r) => (
              <tr key={r._id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{r.receipt_no}</td>
                <td className="px-3 py-2">{fmtIST(r.paid_at)}</td>
                <td className="px-3 py-2">{fmtIST(r.voided_at)}</td>
                <td className="px-3 py-2 text-xs">{r.void_reason}</td>
                <td className="px-3 py-2 text-xs">{r.voided_by_name || "—"}</td>
                <td className="px-3 py-2 text-right">{INR(r.amount)}</td>
              </tr>
            ))}
            {(data.items || []).length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No voided receipts.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =========================================================================
// Catalog management
// =========================================================================

export function Catalog() {
  const [types, setTypes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ code: "", display_name: "", default_amount: "", description: "", display_order: 100 });
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/school/special-collections/types").then(({ data }) => setTypes(data.items || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitNew = async () => {
    if (!form.code.trim() || !form.display_name.trim()) {
      toast.error("Code and display name required"); return;
    }
    try {
      await api.post("/school/special-collections/types", {
        code: form.code, display_name: form.display_name,
        default_amount: form.default_amount ? parseFloat(form.default_amount) : null,
        description: form.description || null,
        display_order: parseInt(form.display_order, 10) || 100,
      });
      setShowAdd(false);
      setForm({ code: "", display_name: "", default_amount: "", description: "", display_order: 100 });
      load();
      toast.success("Type created");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitEdit = async () => {
    try {
      await api.patch(`/school/special-collections/types/${editing._id}`, {
        display_name: editing.display_name,
        default_amount: editing.default_amount,
        description: editing.description,
        display_order: editing.display_order,
        is_active: editing.is_active,
      });
      setEditing(null);
      load();
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="sc-catalog">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Collection types</h3>
        <Button onClick={() => setShowAdd(true)} data-testid="sc-catalog-add"><Plus size={14} className="mr-1" />Add type</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Name</th>
              <th className="text-right px-3 py-2">Default ₹</th>
              <th className="text-right px-3 py-2">Order</th>
              <th className="px-3 py-2">Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {types.map((t) => (
              <tr key={t._id} className={`border-t border-slate-100 ${!t.is_active ? "opacity-60" : ""}`} data-testid={`sc-catalog-row-${t.code}`}>
                <td className="px-3 py-2 font-mono text-xs">{t.code}</td>
                <td className="px-3 py-2">{t.display_name}</td>
                <td className="px-3 py-2 text-right">{t.default_amount != null ? INR(t.default_amount) : "—"}</td>
                <td className="px-3 py-2 text-right">{t.display_order}</td>
                <td className="px-3 py-2">
                  <Badge variant={t.is_active ? "default" : "outline"}>{t.is_active ? "Active" : "Inactive"}</Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...t })}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showAdd} onOpenChange={(o) => !o && setShowAdd(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New collection type</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code (slug)</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. LIBRARY_FINE" />
            </div>
            <div>
              <Label>Display name</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Default amount</Label>
                <Input type="number" min="0" value={form.default_amount}
                       onChange={(e) => setForm({ ...form, default_amount: e.target.value })} />
              </div>
              <div>
                <Label>Display order</Label>
                <Input type="number" value={form.display_order}
                       onChange={(e) => setForm({ ...form, display_order: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={submitNew}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit type</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Renaming is allowed. Historical receipts will keep their original name.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Display name</Label>
                <Input value={editing.display_name}
                       onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Default amount</Label>
                  <Input type="number" value={editing.default_amount ?? ""}
                         onChange={(e) => setEditing({ ...editing, default_amount: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                </div>
                <div>
                  <Label>Display order</Label>
                  <Input type="number" value={editing.display_order ?? 0}
                         onChange={(e) => setEditing({ ...editing, display_order: parseInt(e.target.value, 10) || 0 })} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={2} value={editing.description ?? ""}
                          onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.is_active}
                       onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                Active
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={submitEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================================
// Default routes export
// =========================================================================

export function SpecialCollectionsRoutes() {
  return (
    <Routes>
      <Route element={<SpecialCollectionsLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="collect" element={<Collect />} />
        <Route path="daily-register" element={<DailyRegister />} />
        <Route path="by-type" element={<ByType />} />
        <Route path="by-cashier" element={<ByCashier />} />
        <Route path="by-payer" element={<ByPayer />} />
        <Route path="void-log" element={<VoidLog />} />
        <Route path="catalog" element={<Catalog />} />
      </Route>
    </Routes>
  );
}

// =========================================================================
// Feature-flag probe — used by sidebar to conditionally render the nav group
// =========================================================================

export function useSpecialCollectionsEnabled() {
  const [enabled, setEnabled] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    if (!user || !["school_admin", "cashier", "super_admin"].includes(user.role)) {
      setEnabled(false); return;
    }
    api.get("/school/special-collections/types")
      .then(() => setEnabled(true))
      .catch(() => setEnabled(false));
  }, [user]);
  return enabled;
}
